import { DateTime } from 'luxon';

function uses12HourClock(): boolean {
  const formatter = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: 'numeric' });
  const parts = formatter.formatToParts(new Date(Date.UTC(2020, 0, 1, 13, 0)));
  return parts.some((p) => p.type === 'dayPeriod');
}

export function formatLocalTime(timeOrIso: string): string {
  const isHHmm = /^\d{2}:\d{2}$/.test(timeOrIso);
  const dt = isHHmm ? DateTime.fromFormat(timeOrIso, 'HH:mm') : DateTime.fromISO(timeOrIso);
  if (!dt.isValid) {
    return timeOrIso;
  }

  if (uses12HourClock()) {
    const hasMinutes = dt.minute !== 0;
    const fmt = hasMinutes ? 'h:mma' : 'ha';
    return dt.toFormat(fmt).replace('AM', 'am').replace('PM', 'pm');
  }
  return dt.toFormat('HH:mm');
}

export function formatLocalTimeNoTz(timeOrIso: string): string {
  const isHHmm = /^\d{2}:\d{2}$/.test(timeOrIso);
  const dt = isHHmm ? DateTime.fromFormat(timeOrIso, 'HH:mm') : DateTime.fromISO(timeOrIso, { setZone: true });
  if (!dt.isValid) {
    return timeOrIso;
  }

  if (uses12HourClock()) {
    const hasMinutes = dt.minute !== 0;
    const fmt = hasMinutes ? 'h:mma' : 'ha';
    return dt.toFormat(fmt).replace('AM', 'am').replace('PM', 'pm');
  }
  return dt.toFormat('HH:mm');
}

/** Returns HH:mm as written in the ISO string, without timezone conversion. Use when the displayed time should match the literal value (e.g. departure time at origin). */
export function formatTimeNoTz(timeOrIso: string): string {
  const isHHmm = /^\d{2}:\d{2}$/.test(timeOrIso);
  if (isHHmm) {
    return timeOrIso;
  }

  const dt = DateTime.fromISO(timeOrIso, { setZone: true });
  if (!dt.isValid) {
    return timeOrIso;
  }

  return dt.toFormat('HH:mm');
}

export function formatLocalDate(dateISO: string): string {
  const dt = DateTime.fromISO(dateISO, { setZone: true });
  if (!dt.isValid) {
    return dateISO;
  }

  // Locale-aware short month, 2-digit day, numeric year (e.g., 05 Jan 2025), localized order
  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(dt.toJSDate());
}

/** Formats a date as written in the ISO string, without timezone conversion. */
export function formatLocalDateNoTz(dateISO: string): string {
  const dt = DateTime.fromISO(dateISO, { setZone: true });
  if (!dt.isValid) {
    return dateISO;
  }

  // Build a UTC date from the date components to avoid timezone shift when formatting.
  const utcDate = new Date(Date.UTC(dt.year, dt.month - 1, dt.day, 12, 0, 0));

  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(utcDate);
}

export function formatLocalDateWithoutYear(dateISO: string): string {
  const dt = DateTime.fromISO(dateISO, { setZone: true });
  if (!dt.isValid) {
    return dateISO;
  }

  // Locale-aware short month, 2-digit day, numeric year (e.g., 05 Jan 2025), localized order
  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: 'short',
  }).format(dt.toJSDate());
}

export function formatLocalDateTime(dateISO: string): string {
  const dt = DateTime.fromISO(dateISO);
  if (!dt.isValid) {
    return dateISO;
  }

  const dateText = formatLocalDate(dateISO);

  const hasMinutes = dt.minute !== 0;
  const timeText = uses12HourClock()
    ? dt
        .toFormat(hasMinutes ? 'h:mma' : 'ha')
        .replace('AM', 'am')
        .replace('PM', 'pm')
    : dt.toFormat('H:mm');

  return `${dateText} ${timeText}`;
}
